import { useContext, useEffect, useRef, useState } from "react";
import { fetchDocument } from "../services/Api";
import { AppContext } from "../contexts/AppContext";

/**
 * Fetch article from the API
 * @param {number|string|undefined} id Document ID 
 * @param {object} previewData Preview object
 * @param {boolean} restartOnError Wait 3 seconds before load
 * @returns {object} Article object
 */
export default function useFetchDocument(id: number|string|undefined, previewData?: any, restartOnError?: Boolean) {
  const {forceUpdate, setForceUpdate} = useContext(AppContext);
  const isSubscribed = useRef<boolean>(true);
  const lastId = useRef<string | number>(undefined);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastPreviewData, setLastPreviewData] = useState<any>(null);

  function updateDoc() {
    setData({});
    setIsLoading(true);

    (async() => {
      fetchDocument(String(id)).then(document => {
        if (isSubscribed.current) {
          if (document) {
            setData(document);
          } else {
            if (restartOnError) {
              setTimeout(() => {
                window.location.reload();
              }, 5000);
            }
            setData({status: "error"});
          }
          
          setIsLoading(false);
        }
      }).catch(e => {
        setError(e);
      });

      return () => { 
        isSubscribed.current = false;
        lastId.current = undefined;
      }
    })();
  }

  useEffect(() => {
    isSubscribed.current = true;

    if (!id || isNaN(Number(id)) || Number(lastId?.current) === Number(id) || (previewData)) { return; }
    lastId.current = id;
    updateDoc();

    // eslint-disable-next-line
  }, [id, data, previewData, restartOnError]);

  useEffect(() => {
    if (previewData) {
      // Avoid rerendering if the same data is received
      if (lastPreviewData === previewData) { return; }
      setLastPreviewData(previewData);

      // Inject documentId, ownerId and ownerSlug for statistics tracking
      setData({content: previewData, status: "published", id: previewData?.meta?.id, owner_id: previewData?.meta?.owner_id, owner_slug: previewData?.meta?.owner_slug});
    }
  }, [previewData, lastPreviewData]);

  useEffect(() => {
    if(!!forceUpdate && forceUpdate.type === "document" && parseInt(forceUpdate.documentId) === id) {
      isSubscribed.current = true;
      updateDoc();
      setForceUpdate(null);
    };
    // eslint-disable-next-line
  }, [forceUpdate])

  if (!id && !previewData) {
    return [null, null, false];
  }

  return [data, error, isLoading]
}